import "./sessionSummaryPromptConsole.css";
import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import { Alert, Button, Form, Card, Badge } from "react-bootstrap";
import { logError, LogCategory } from "utils/sentryLogger";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner, faSearch } from "@fortawesome/free-solid-svg-icons";
import { colors, constants, generateRoutePath } from "utils";
import { useAuthUserData } from "hooks/useAuthUserData/useAuthUserData";
import { request } from "hooks/useOkoApi/useOkoApi";
import IconInfo from "assets/Icon-Info.svg";

export const SemanticSearch = () => {
  const history = useHistory();
  const { authUserData, loading } = useAuthUserData();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [results, setResults] = useState(null);
  const [requestDuration, setRequestDuration] = useState(null);

  if (loading) return <></>;
  if (authUserData.isAnonymous) history.push(generateRoutePath(constants.ADMIN_LOGIN_ROUTE_NAME));

  const handleSearch = async (e) => {
    e.preventDefault();
    
    // Only search if query is at least 3 characters
    if (searchQuery.trim().length < 3) {
      setError("Please enter at least 3 characters to search");
      return;
    }

    setIsLoading(true);
    setError(null);
    setResults(null);
    setRequestDuration(null);

    const startTime = performance.now();

    try {
      const response = await request("standards/semantic-search", {
        method: "POST",
        payload: {
          query: searchQuery.trim(),
          limit: 50,
          similarity_threshold: 0.2,
        },
      });

      const endTime = performance.now();
      const duration = endTime - startTime;
      setRequestDuration(duration);
      setResults(response);
    } catch (e) {
      logError("Admin semantic search failed", e, { category: LogCategory.ERROR });
      setError(e?.detail || e?.message || "An error occurred while searching");
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    
    // Clear error if user is typing and has less than 3 chars
    if (value.length < 3 && error) {
      setError(null);
    }
  };

  const getSimilarityColor = (score) => {
    if (score >= 0.8) return "success";
    if (score >= 0.6) return "primary";
    if (score >= 0.4) return "warning";
    return "secondary";
  };

  return (
    <div style={{
      display: "flex",
      backgroundColor: colors.milkGlass200,
      flexDirection: "column",
      gap: "24px"
    }}>
      <div style={{
        alignItems: "center",
        backgroundColor: "white",
        borderRadius: "12px",
        boxShadow: "0px 1px 2px 0px rgba(14, 26, 49, 0.05)"
      }}>
        <Alert style={{ marginBottom: 0 }} variant="">
          <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            <div style={{ display: "flex", gap: "16px" }}>
              <div style={{
                alignItems: "center",
                display: "flex",
                minHeight: "36px",
                gap: "12px",
                flex: "1 0 0",
                paddingRight: "28px"
              }}>
                <img src={IconInfo} alt="info" />
                Search for standards using semantic similarity. Enter at least 3 characters to begin searching.
              </div>
            </div>
          </div>
        </Alert>
      </div>

      <div style={{
        backgroundColor: "white",
        borderRadius: "12px",
        boxShadow: "0px 1px 2px 0px rgba(14, 26, 49, 0.05)",
        padding: "24px"
      }}>
        <Form onSubmit={handleSearch}>
          <Form.Group>
            <Form.Label style={{ fontWeight: 600 }}>Search Query</Form.Label>
            <div style={{ display: "flex", gap: "12px" }}>
              <Form.Control
                type="text"
                placeholder="Enter search query (min 3 characters)..."
                value={searchQuery}
                onChange={handleInputChange}
                disabled={isLoading}
                style={{ flex: 1 }}
              />
              <Button
                type="submit"
                disabled={isLoading || searchQuery.trim().length < 3}
                style={{
                  backgroundColor: colors.nightlyWoods500,
                  border: "none",
                  minWidth: "120px"
                }}
              >
                {isLoading ? (
                  <FontAwesomeIcon icon={faSpinner} spin />
                ) : (
                  <>
                    <FontAwesomeIcon icon={faSearch} className="mr-2" />
                    Search
                  </>
                )}
              </Button>
            </div>
            {error && (
              <Alert style={{ marginTop: "12px", marginBottom: 0 }} variant="danger">
                {error}
              </Alert>
            )}
          </Form.Group>
        </Form>
      </div>

      {results && (
        <div style={{
          backgroundColor: "white",
          borderRadius: "12px",
          boxShadow: "0px 1px 2px 0px rgba(14, 26, 49, 0.05)",
          padding: "24px"
        }}>
          <h5 style={{ marginBottom: "16px" }}>
            Search Results for: <em>"{results.query}"</em>
          </h5>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "24px" }}>
            <p style={{ color: colors.nightlyWoods700, marginBottom: 0 }}>
              Found {results.results.length} result{results.results.length !== 1 ? 's' : ''}
            </p>
            {requestDuration !== null && (
              <p style={{ color: colors.nightlyWoods700, marginBottom: 0, fontSize: "14px" }}>
                Request completed in <strong>{requestDuration.toFixed(0)}ms</strong>
              </p>
            )}
          </div>

          {results.results.length === 0 ? (
            <Alert variant="info">
              No results found. Try adjusting your search query.
            </Alert>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
              {results.results.map((result, index) => (
                <Card key={index} style={{ border: `1px solid ${colors.milkGlass200}` }}>
                  <Card.Body>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", marginBottom: "12px" }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: "flex", gap: "8px", alignItems: "center", marginBottom: "8px" }}>
                          <Badge bg={getSimilarityColor(result.similarity_score)}>
                            {(result.similarity_score * 100).toFixed(1)}% match
                          </Badge>
                          <Badge bg="secondary">{result.entity_type}</Badge>
                          {result.grade && <Badge bg="info">Grade {result.grade}</Badge>}
                        </div>
                        <h6 style={{ marginBottom: "8px", fontWeight: 600 }}>
                          {result.code}
                        </h6>
                        <p style={{ marginBottom: "8px", fontWeight: 500 }}>
                          {result.name}
                        </p>
                        {result.description && (
                          <p style={{ marginBottom: "12px", color: colors.nightlyWoods700 }}>
                            {result.description}
                          </p>
                        )}
                      </div>
                    </div>
                    
                    {(result.domain_name || result.cluster_name) && (
                      <div style={{
                        borderTop: `1px solid ${colors.milkGlass200}`,
                        paddingTop: "12px",
                        fontSize: "14px",
                        color: colors.nightlyWoods700
                      }}>
                        {result.domain_name && (
                          <div style={{ marginBottom: "4px" }}>
                            <strong>Domain:</strong> {result.domain_code} - {result.domain_name}
                          </div>
                        )}
                        {result.cluster_name && (
                          <div>
                            <strong>Cluster:</strong> {result.cluster_code} - {result.cluster_name}
                          </div>
                        )}
                      </div>
                    )}
                  </Card.Body>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}
      {isLoading && <div style={{ alignSelf: "center", position: "fixed", top: "50%" }}>
        <FontAwesomeIcon
          icon={faSpinner}
          spin
          size="5x"
        />
      </div>}
    </div>
  );
};

